import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { HeaderNav } from "@/components/HeaderNav";
import api from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

const MAX_TRIES = 10;

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("polling"); // polling | paid | failed

  const poll = useCallback(async (tries) => {
    if (!sessionId) { setState("failed"); return; }
    try {
      const { data } = await api.get(`/payments/status/${sessionId}`);
      if (data.payment_status === "paid") {
        setState("paid");
        toast.success("Payment confirmed. Your assessment is unlocked.");
        setTimeout(() => navigate("/consent", { replace: true }), 1200);
        return;
      }
      if (["failed", "expired"].includes(data.payment_status)) { setState("failed"); return; }
    } catch { /* keep trying */ }
    if (tries >= MAX_TRIES) { setState("failed"); return; }
    setTimeout(() => poll(tries + 1), 2000);
  }, [sessionId, navigate]);

  useEffect(() => { poll(0); }, [poll]);

  return (
    <div>
      <HeaderNav />
      <div className="max-w-xl mx-auto px-5 pt-24 text-center cg-fade-up" data-testid="payment-success-page">
        {state === "polling" && (
          <><Loader2 className="w-10 h-10 text-brass animate-spin mx-auto mb-5" />
            <h1 className="font-serif text-3xl text-[#F3F4F6]">Confirming your payment…</h1>
            <p className="text-[#9CA3AF] mt-3">This only takes a moment.</p></>
        )}
        {state === "paid" && (
          <><CheckCircle2 className="w-12 h-12 text-[#7D9D86] mx-auto mb-5" />
            <h1 className="font-serif text-3xl text-[#F3F4F6]" data-testid="payment-success-confirmed">Payment confirmed</h1>
            <p className="text-[#9CA3AF] mt-3">Taking you to your assessment…</p></>
        )}
        {state === "failed" && (
          <><XCircle className="w-12 h-12 text-terracotta mx-auto mb-5" />
            <h1 className="font-serif text-3xl text-[#F3F4F6]">We could not confirm your payment</h1>
            <p className="text-[#9CA3AF] mt-3 mb-6">If you were charged, it may take a moment to reflect. You can check your access from the paywall.</p>
            <button data-testid="payment-retry-button" onClick={() => navigate("/payment", { replace: true })}
              className="bg-brass text-obsidian font-medium px-7 py-3 rounded-full cg-scale-btn hover:bg-amber2">Back to checkout</button></>
        )}
      </div>
    </div>
  );
}
